import React from 'react';
import { Alert, Card, Flex, Typography, theme } from 'antd';
import { useNavigate } from 'react-router-dom';

import type { GscConnectionDeadEndReason } from '../SearchPerformanceAnalyticsEvents';

type Props = {
  reason: GscConnectionDeadEndReason;
  /** Shown for `no_matching_properties`, so the mismatch is visible rather than asserted. */
  unmatchedSiteUrls?: string[];
  lostUrls?: string[];
};

const DEAD_END_COPY: Record<
  GscConnectionDeadEndReason,
  { title: string; message: string; description: string }
> = {
  no_properties: {
    title: 'No Search Console properties on this Google account',
    message: 'This Google account has no Search Console properties',
    description:
      'Add and verify your site in Google Search Console first — Prerender can only sync properties the account already has access to. If you manage Search Console with a different Google account, sign in with that one instead.',
  },
  no_matching_properties: {
    title: 'No matching domains to sync',
    message: 'None of your Search Console properties match a Prerender domain',
    description:
      "A domain qualifies once it's added to Prerender, its integration is verified, and it has rendered pages. Check that the domain below is set up in Prerender, or sign in with the Google account that manages it.",
  },
  insufficient_permission: {
    title: 'No domains available to sync',
    message: 'This Google account cannot read your properties in Search Console',
    description:
      'Search Console lists the matching properties for this account, but reading performance data needs Owner or Full access. Ask a Search Console owner to upgrade this account, then connect again.',
  },
  session_expired: {
    title: 'Connection link expired',
    message: 'This connection link has expired or was already used',
    description:
      'Connection links last only a few minutes and work once. Sign in with Google again to pick your domains.',
  },
  session_load_failed: {
    title: "Couldn't load your domains",
    message: "We couldn't load your Search Console properties",
    description:
      'Your Google sign-in is fine — fetching the properties failed on our side. Try again in a moment.',
  },
  property_access_lost: {
    title: 'No domains available to sync',
    message: 'Search Console no longer shares your synced property with this Google account',
    description:
      'Add it back in Google Search Console (or restore this account\'s permission on it) and sign in again to resume syncing. If it now lives under a different Google account, sign in with that one instead.',
  },
};

function SearchPerformanceDomainDeadEnd({ reason, unmatchedSiteUrls = [], lostUrls = [] }: Props) {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const copy = DEAD_END_COPY[reason];
  const showsMismatch = reason === 'no_matching_properties' && unmatchedSiteUrls.length > 0;
  const showsLost = reason === 'property_access_lost' && lostUrls.length > 0;

  return (
    <Card title={copy.title} styles={{ body: { paddingTop: token.padding } }}>
      <Flex vertical gap={token.margin}>
        <Alert
          type={reason === 'session_expired' || reason === 'session_load_failed' ? 'error' : 'info'}
          showIcon
          message={copy.message}
          description={copy.description}
        />

        {showsLost && (
          <Flex vertical gap={token.marginXS}>
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              No longer shared with this Google account:
            </Typography.Text>
            {lostUrls.map((siteUrl) => (
              <Typography.Text key={siteUrl} code>
                {siteUrl}
              </Typography.Text>
            ))}
          </Flex>
        )}

        {showsMismatch && (
          <Flex vertical gap={token.marginXS}>
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              Found in Search Console, not matched to a Prerender domain:
            </Typography.Text>
            {unmatchedSiteUrls.map((siteUrl) => (
              <Typography.Text key={siteUrl} code>
                {siteUrl}
              </Typography.Text>
            ))}
            <Typography.Link
              onClick={() => navigate('/domain-manager')}
              style={{ color: token.colorPrimary }}
            >
              Review your Prerender domains
            </Typography.Link>
          </Flex>
        )}
      </Flex>
    </Card>
  );
}

export default SearchPerformanceDomainDeadEnd;
